import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ApiResponse } from '../../models/api-response.model';
import { EmployerCreditModel } from '../../models/employer-credit.model';
import { CreditLineRequestModel } from '../../models/credit-line-request.model';
import { RolloverPercentage } from '../../models/rollover-percentage';
import { RolloverDay } from '../../models/rollover-day';
import { PaginatedResponse } from '../../models/paginated-response';

@Injectable({ providedIn: 'root' })
export class CreditManagementService {
  private readonly apiUrl = `${environment.apiGatewayBaseUrl}/CreditManagement`;
  private readonly companiesUrl = `${environment.apiGatewayBaseUrl}/Companies`;
  private readonly rolloverDataUrl = `${environment.apiGatewayBaseUrl}/RollOvers`;

  constructor(private http: HttpClient) { }

  // Get Roolover Percentages
  getRolloverPercentages(): Observable<ApiResponse<Array<RolloverPercentage>>> {
    // GET - /RollOvers/percentages
    const url = `${this.rolloverDataUrl}/percentages`;
    return this.http.get<ApiResponse<Array<RolloverPercentage>>>(url);
  }

  // Get Roolover Days
  getRolloverDays(): Observable<ApiResponse<Array<RolloverDay>>> {
    // GET - /RollOvers/days
    const url = `${this.rolloverDataUrl}/days`;
    return this.http.get<ApiResponse<Array<RolloverDay>>>(url);
  }

  // Employer Credits list (tab 1)
  getEmployerCredits(): Observable<ApiResponse<EmployerCreditModel[]>> {
    // GET - /Companies/credit-line-rollover-details
    const url = `${this.companiesUrl}/credit-line-rollover-details`;
    return this.http.get<ApiResponse<EmployerCreditModel[]>>(url);
  }

  // Employer credit line & rollover details by company (optional usage)
  getCreditLineRolloverDetails(companyId: string | number): Observable<ApiResponse<any[]>> {
    const url = `${this.companiesUrl}/credit-line-rollover-details?companyId=${companyId}`;
    return this.http.get<ApiResponse<any[]>>(url);
  }

  // Credit Line Requests candidates (tab 2)
  getCompaniesWithoutCreditLine(): Observable<ApiResponse<any[]>> {
    // GET - /Companies/companies/without-credit-line
    //  const url = `${this.companiesUrl}/companies/without-credit-line`;
    const url = `${this.companiesUrl}/companies/credit-line-rollover-request/no-or-rejected`;
    return this.http.get<ApiResponse<any[]>>(url);
  }

  // Pending Approvals (tab 3)
  getPendingCreditLineApprovals(): Observable<ApiResponse<CreditLineRequestModel[]>> {
    // GET - /Companies/credit-line-rollover-request
    const url = `${this.companiesUrl}/credit-line-rollover-request`;
    return this.http.get<ApiResponse<CreditLineRequestModel[]>>(url);
  }

  // Approve/Reject credit line rollover request (isApproved flag)
  approveCreditLineRolloverRequest(params: { companyId: string | number; companyCRId: string | number; approvedBy: string | number; isApproved: boolean; }): Observable<ApiResponse<boolean>> {
    const { companyId, companyCRId, approvedBy, isApproved } = params;
    const url = `${this.companiesUrl}/credit-line-rollover-request/approve?companyId=${companyId}&companyCRId=${companyCRId}&approvedBy=${approvedBy}&isApproved=${isApproved}`;
    // Using POST with no body; adjust if API expects GET or PATCH
    return this.http.post<ApiResponse<boolean>>(url, {});
  }

  // Backwards-compat wrapper if needed
  rejectCreditLineRolloverRequest(params: { companyId: string | number; companyCRId: string | number; rejectedBy: string | number; reason?: string; }): Observable<ApiResponse<boolean>> {
    const { companyId, companyCRId, rejectedBy } = params;
    return this.approveCreditLineRolloverRequest({ companyId, companyCRId, approvedBy: rejectedBy, isApproved: false });
  }

  // Create a new credit line rollover request (Send for Approval)
  createCreditLineRolloverRequest(companyId: string | number, payload: {
    companyId: number | string;
    creditLimit: number;
    wageWithdrawalPercentage: number;
    rollOverDayId: number;
    rollOverPercentageId: number;
    requestedBy: number | string;
  }): Observable<ApiResponse<boolean>> {
    const url = `${this.companiesUrl}/companies/${companyId}/credit-line-rollover-requests`;
    return this.http.post<ApiResponse<boolean>>(url, payload);
  }


  // ======================= Paginated ======================== //

  getEmployerCreditsPaginated(
    pageNumber?: number, pageSize?: number,
    companyId?: number | string | null,
    startDate?: string | null, endDate?: string | null
  ): Observable<PaginatedResponse<EmployerCreditModel>> {
    let params = new HttpParams();
    if (pageNumber) params = params.set('pageNumber', pageNumber);
    if (pageSize) params = params.set('pageSize', pageSize);
    if (companyId) params = params.set('companyId', companyId);
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<PaginatedResponse<EmployerCreditModel>>(
      `${this.companiesUrl}/credit-line-rollover-details/paginated`, { params });
  }

  getCreditLineRequestsPaginated(
    pageNumber?: number, pageSize?: number,
    companyId?: number | string | null,
    startDate?: string | null, endDate?: string | null
  ): Observable<PaginatedResponse<any>> {
    let params = new HttpParams();
    if (pageNumber) params = params.set('pageNumber', pageNumber);
    if (pageSize) params = params.set('pageSize', pageSize);
    if (companyId) params = params.set('companyId', companyId);
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<PaginatedResponse<any>>(
      `${this.companiesUrl}/companies/credit-line-rollover-request/no-or-rejected/paginated`, { params });
  }

  getPendingApprovalsPaginated(
    pageNumber?: number, pageSize?: number,
    companyId?: number | string | null,
    startDate?: string | null, endDate?: string | null
  ): Observable<PaginatedResponse<any>> {
    let params = new HttpParams();
    if (pageNumber) params = params.set('pageNumber', pageNumber);
    if (pageSize) params = params.set('pageSize', pageSize);
    if (companyId) params = params.set('companyId', companyId);
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<PaginatedResponse<any>>(
      `${this.companiesUrl}/credit-line-rollover-request/paginated`, { params });
  }

}


